import { getInitialLoanFields, InputType } from 'config'
import { canAddNewField, InputTypeOfLoans, isCustomFieldKey } from 'config/loan.input.fields.constants'
import { FieldOrigin, fieldOriginOptions, isCustomOrigin, LoanFieldProps } from 'config/loan.input.visibility.type'
import { defaultInputs as AdditionalMemberInputs } from 'pages/LoanApplication/AdditionalMember/constants'
import { defaultInputs as additionalPropertyAddressInputs } from 'pages/LoanApplication/AdditionalPropertyInformation/constant'
import { defaultInputs as borrowerInformationInputs } from 'pages/LoanApplication/BorrowerInformation/constants'
import { defaultInputs as DeclarationHMDAInputs } from 'pages/LoanApplication/DeclarationsHmda/constants'
import { defaultInputs as propertyInformationInputs } from 'pages/LoanApplication/PropertyInformation/constants'
import { SIGN_ACTION_KEYS } from 'pages/LoanApplication/SignApplication/constants'
import { defaultInputs as trackRecordInputs } from 'pages/LoanApplication/TrackRecord/constants'
import { defaultInputs as reoInputs } from 'pages/LoanApplication/TrackRecord/RentalEstate/constants'
import { Input2 } from 'stories/components'

import { CreditScoreTemplate } from './CreditScoreTemplate'
import { LoanApplicationSignTemplate } from './LoanApplicationSignTemplate'

export const assetLiabilityInputs = () => {
  return {
    assetInformation: {
      inputType: 'ToggleButton',
      title: 'Asset Information',
    },
    totalCredits: {
      inputType: 'ToggleButton',
      title: 'Total Credits',
    },
    liabilityInformation: {
      inputType: 'ToggleButton',
      title: 'Liability Information',
    },
  }
}

export const TrackRecordInputs = () => {
  const data: Record<string, string> = {
    purchaseDateBeforeLoanDate: `Purchase Date must be before Loan Date.`,
    addressDifferentPropertyAddr: `Address cannot be the same as Subject Property Address.`,
    purchaseDateBeforeSoldDate: `Purchase Date must be before Sold Date.`,
    soldDateBeforeLoanDate: `Sold Date must be before Loan Date.`,
  }
  let result: Record<string, InputType> = {
    sectionForNo: {
      inputType: 'Section',
      title: 'Retained As Rental? - No',
      span: 'full',
    },
  }
  Object.keys(data).forEach((key) => {
    result[key] = {
      inputType: 'ToggleButton',
      title: data[key],
    }
  })

  const additionalData: Record<string, string> = {
    soldDateWithin30monthsLoanDate: `Sold Date must be within {value} months of Loan Date.`, // 36
    purchaseDateWithin24monthsSoldDate: `Purchase Date must be within {value} months of Sold Date.`, // 24
    soldPriceLessthan75AfterRepairValue: `Sold Price, cannot be less than {value}% of After Repair Value.`, // 75
  }
  const defaultValues: Record<string, number> = {
    soldDateWithin30monthsLoanDate: 36,
    purchaseDateWithin24monthsSoldDate: 24,
    soldPriceLessthan75AfterRepairValue: 75,
  }

  const addAdditionalData = (key: string, title: string, defaultValue: number) => {
    result[key] = {
      inputType: 'custom',
      title,
      render: (value: any, onChange: Function, fieldInput?: { additionalElements?: any }) => {
        if (!value || typeof value !== 'object')
          value = {
            enabled: true,
            value: defaultValue,
          }
        return (
          <Input2
            title={title}
            value={value.value ?? ''}
            additionalElements={fieldInput?.additionalElements}
            onChange={(v: string) => onChange({ ...value, value: v })}
          />
        )
      },
    }
  }
  Object.keys(additionalData).forEach((key) => addAdditionalData(key, additionalData[key], defaultValues[key]))

  result['sectionForYes'] = {
    inputType: 'Section',
    title: 'Retained As Rental? - Yes',
    span: 'full',
  }
  ;['purchaseDateBeforeLoanDate', 'addressDifferentPropertyAddr'].forEach((key) => {
    result[`${key}Yes`] = {
      inputType: 'ToggleButton',
      title: data[key],
    }
  })
  result = {
    ...result,
    purchasePrice: {
      inputType: 'text',
      type: 'thousandSep',
      title: 'Purchase Price',
      required: true,
      prefix: '$',
    },
    purchaseDate: {
      inputType: 'text',
      type: 'date',
      title: 'Purchase Date',
      required: true,
    },
    renovationBudget: {
      inputType: 'text',
      type: 'thousandSep',
      title: 'Renovation Budget',
      required: true,
      prefix: '$',
    },
  }

  const additionalDataYes: Record<string, string> = {
    purchaseDateWithin24monthsExistingClosingDate: `Purchase Date must be within {value} months of existing closing date.`, // 24
    purchasePriceLessthan75TransactionPurPrice: `Purchase Price must be within {value}% of transaction Purchase Price.`, // 75
    purchaseDateFrom24monthsExistingClosingDate: `Purchase Date must be at least {value} months from existing closing date.`, // 12
  }
  const defaultValuesYes: Record<string, number> = {
    purchaseDateWithin24monthsExistingClosingDate: 24,
    purchasePriceLessthan75TransactionPurPrice: 75,
    purchaseDateFrom24monthsExistingClosingDate: 12,
  }
  Object.keys(additionalDataYes).forEach((key) => addAdditionalData(key, additionalDataYes[key], defaultValuesYes[key]))

  let formInputs = trackRecordInputs()
  result['sectionForFormTrackRecord'] = {
    inputType: 'Section',
    title: 'Table Form (Track Record)',
    span: 'full',
  }
  result['hasTrackRecord'] = {
    inputType: 'ToggleButton',
    title: 'Has Track Record?',
    value: true,
  }
  Object.keys(formInputs).forEach((key) => (result[`_${key}`] = formInputs[key]))

  result['sectionForFormReo'] = {
    inputType: 'Section',
    title: 'Table Form (Real Estate Owned)',
    span: 'full',
  }
  formInputs = reoInputs({})
  Object.keys(formInputs).forEach((key) => (result[`-${key}`] = formInputs[key]))

  return result
}

export const addCustomInputs = (inputs: InputTypeOfLoans, loanFieldsProps: LoanFieldProps) => {
  const oldCustomInfoOriginKeys = Object.keys(inputs).filter((origin) => isCustomOrigin(origin))
  oldCustomInfoOriginKeys.forEach((customOriginKey) => delete (inputs as any)[customOriginKey])

  const customInfoOriginKeys = Object.keys(loanFieldsProps).filter((origin) => canAddNewField(origin as any))

  customInfoOriginKeys.forEach((customOriginKey) => {
    const customFields = loanFieldsProps[customOriginKey] || {}
    const customInputs: Record<string, InputType> = (inputs as any)[customOriginKey] || {}
    Object.keys(customFields).forEach((fieldKey) => {
      // if (customInputs[fieldKey]) {
      //   const inputType = customInputs[fieldKey].inputType
      //   const fieldType = customFields[fieldKey].inputType
      //   if (!inputType || !fieldType || inputType == fieldType) return
      // }

      const field = customFields[fieldKey]
      if (field && (customInputs[fieldKey] as any) && !field.options?.length && (customInputs[fieldKey] as any).options)
        delete field.options

      if (
        field.inputType == 'Select' &&
        isCustomFieldKey(fieldKey) &&
        (field as any).customOptions &&
        field.options === undefined
      )
        (field as any).options = (field as any).customOptions

      let isSetLinkedField = false
      if (field.linkedField) {
        const { fieldOrigin: linkedFieldOrigin, fieldKey: linkedFieldKey } = field.linkedField
        if (linkedFieldOrigin && linkedFieldKey) {
          const linkedInput = inputs[linkedFieldOrigin as FieldOrigin][linkedFieldKey as string]
          if (linkedInput) {
            customInputs[fieldKey] = {
              ...field,
              ...linkedInput,
            } as any
            isSetLinkedField = true
          }
        }
      }
      if (!isSetLinkedField) {
        customInputs[fieldKey] = {
          inputType: 'text',
          ...((customInputs[fieldKey] as any) || {}),
          ...field,
        } as any
      }
    })
    ;(inputs as any)[customOriginKey] = customInputs
  })

  return inputs
}

const signInputs = (): Record<string, InputType> => {
  return {
    template: {
      inputType: 'custom',
      title: 'Acknowledgement Template',
      render: (value: any, onChange: Function) => <LoanApplicationSignTemplate value={value} onChange={onChange} />,
      span: 'full',
    },
    originatorInformation: {
      inputType: 'ToggleButton',
      title: 'Loan Originator Information',
      span: 'full',
    },
    [SIGN_ACTION_KEYS.viewSignedApplicationPdf]: {
      inputType: 'ActionButton',
      title: 'View Signed Application PDF',
      buttonStyle: 'outline',
      actionType: 'loanSubmission',
      actionKey: 'viewSignedApplicationPdf',
      clickMode: 'always',
    } as any,
    [SIGN_ACTION_KEYS.sendSignatureRequest]: {
      inputType: 'ActionButton',
      title: 'Send Signature Request',
      buttonStyle: 'primary',
      actionType: 'loanSubmission',
      actionKey: 'sendSignatureRequest',
      clickMode: 'always',
    } as any,
    [SIGN_ACTION_KEYS.viewUnsignedApplicationPdf]: {
      inputType: 'ActionButton',
      title: 'View Unsigned Loan Disclosures in PDF',
      buttonStyle: 'link',
      actionType: 'loanSubmission',
      actionKey: 'viewUnsignedApplicationPdf',
      clickMode: 'always',
    } as any,
    [SIGN_ACTION_KEYS.signApplication]: {
      inputType: 'ActionButton',
      title: 'Click Here to Sign Application',
      buttonStyle: 'primary',
      actionType: 'loanSubmission',
      actionKey: 'signApplication',
      clickMode: 'always',
    } as any,
  }
}

const creditInputs = (): Record<string, InputType> => {
  return {
    template: {
      inputType: 'custom',
      title: 'Credit Score Template',
      render: (value: any, onChange: Function) => <CreditScoreTemplate value={value} onChange={onChange} />,
      span: 'full',
    },
  }
}

export const getInputs = (loanFieldsProps: LoanFieldProps, customInputs = true): InputTypeOfLoans => {
  const result = {
    [FieldOrigin.LoanStructure]: getInitialLoanFields(),
    [FieldOrigin.BorrowerInformation]: borrowerInformationInputs(),
    [FieldOrigin.PropertyInformation]: propertyInformationInputs(),
    [FieldOrigin.AdditionalPropertyAddresses]: additionalPropertyAddressInputs(),
    [FieldOrigin.AssetLiability]: assetLiabilityInputs(),
    [FieldOrigin.TrackRecord]: TrackRecordInputs(),
    [FieldOrigin.DeclarationsHMDA]: DeclarationHMDAInputs(loanFieldsProps),
    ['sign']: signInputs(),
    ['credit']: creditInputs(),
    [FieldOrigin.AdditionalMember]: AdditionalMemberInputs(true),
    ['mlds']: {},
  } as any

  if (!customInputs) return result

  return addCustomInputs(result, loanFieldsProps)
}

export const visibilitySections = {
  [FieldOrigin.BorrowerInformation]: 'borrower',
  [FieldOrigin.PropertyInformation]: 'property',
  [FieldOrigin.AdditionalPropertyAddresses]: 'additionalPropertyAddresses',
  [FieldOrigin.EmploymentAndIncome]: 'employment',
  [FieldOrigin.AssetLiability]: 'asset',
  [FieldOrigin.TrackRecord]: 'track',
  [FieldOrigin.DeclarationsHMDA]: 'hmda',
  sign: 'sign',
  credit: 'credit',
  [FieldOrigin.AdditionalMember]: 'additionalMember',
  mlds: 'mlds',
}

export const extendedFieldOriginOptions = {
  ...fieldOriginOptions,
  sign: 'Acknowledgement & Sign',
  credit: 'Credit Score',
  mlds: 'MLDS',
}

export const isInputTypeChangableFieldOrigins = (fieldOrigin: FieldOrigin) =>
  [
    FieldOrigin.LoanStructure,
    FieldOrigin.BorrowerInformation,
    FieldOrigin.PropertyInformation,
    FieldOrigin.DeclarationsHMDA,
  ].includes(fieldOrigin) || isCustomOrigin(fieldOrigin)

export const hasRequiredProps = (fieldOrigin: FieldOrigin) =>
  isInputTypeChangableFieldOrigins(fieldOrigin) ||
  [FieldOrigin.TrackRecord, FieldOrigin.AdditionalMember, FieldOrigin.AdditionalPropertyAddresses].includes(fieldOrigin)

export const hasCoborrowerRequiredProps = (fieldOrigin: FieldOrigin) =>
  [FieldOrigin.BorrowerInformation, FieldOrigin.DeclarationsHMDA].includes(fieldOrigin)

export const canAddLinkedFieldOrigin = (fieldOrigin: FieldOrigin) =>
  [FieldOrigin.BorrowerInformation, FieldOrigin.PropertyInformation, FieldOrigin.LoanStructure].includes(fieldOrigin) ||
  isCustomOrigin(fieldOrigin)

export const canEditField = (fieldOrigin: FieldOrigin, fieldKey: string) => {
  if (canControlSection(fieldOrigin, fieldKey)) return true

  return fieldOrigin != FieldOrigin.TrackRecord || fieldKey == 'hasTrackRecord'
}

export const canControlSection = (fieldOrigin: FieldOrigin, fieldKey: string) =>
  (FieldOrigin.TrackRecord == fieldOrigin && ['sectionForFormTrackRecord', 'sectionForFormReo'].includes(fieldKey)) ||
  (FieldOrigin.PropertyInformation == fieldOrigin &&
    ['otherMortgageLoans', 'rentalIncome', 'giftsOrGrants'].includes(fieldKey))
